import {Button, Dialog, Form, FormButton, FormField, FormSubmitHandler} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {CurrencyFormatter, useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {FinancingRateMatrixCell, VehicleFinancingType} from '@dms/api/vehicle';
import {KeyFigureValue} from '@dms/features/financial-products';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish} from 'shared/web';

import {financingCellFormSchema, FinancingCellFormValues} from '../schemas/financingCellFormSchema';
import {VehicleFinancingPricing} from '../types/VehicleFinancingPricing';
import {calculateFinancingOffer, FinancingInsurance} from '../utils/calculateFinancingOffer';
import {formatCurrencyOrDash} from '../utils/formatCurrencyOrDash';

interface FinancingCellDialogProps {
  isOpen: boolean;
  type: VehicleFinancingType;
  term: number;
  downPayment: number;
  balloonPayment: number | null;
  cell: FinancingRateMatrixCell | null;
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  insurance: FinancingInsurance;
  isActive: boolean;
  isReadOnly: boolean;
  showCalculation?: boolean;
  allowSetActive?: boolean;
  onClose: () => void;
  onSave: (cell: FinancingRateMatrixCell, setActive: boolean) => void;
  onDelete: (cell: FinancingRateMatrixCell) => void;
}

// The rate-dependent key figures have no value without a usable offer, so they fall back to the
// placeholder while the figures derived from the cell coordinates alone stay visible.
const formatRateDependentAmount = (
  formatCurrency: CurrencyFormatter,
  amount: number | Nullish,
  currency: string
) => (isNil(amount) ? EMPTY_PLACEHOLDER : formatCurrencyOrDash(formatCurrency, amount, currency));

export function FinancingCellDialog(props: FinancingCellDialogProps) {
  const formatCurrency = useFormatCurrency();
  const downPaymentAmount = props.vehiclePrice * (props.downPayment / 100);
  const netCredit = props.vehiclePrice - downPaymentAmount;

  const handleSubmit: FormSubmitHandler<FinancingCellFormValues> = (values) => {
    props.onSave(
      {
        term: props.term,
        downPayment: props.downPayment,
        balloonPayment: props.type === 'BALLOON' ? props.balloonPayment : null,
        nominalRate: String(values.nominalRate),
        apr: String(values.apr),
      },
      values.setActive
    );

    return Promise.resolve();
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={i18n.t('page.vehicle.financingAndLeasing.editVariant')}
      size="small"
    >
      <Form<FinancingCellFormValues>
        experimentalZodSchema={financingCellFormSchema}
        mode="onChange"
        defaultValues={{
          nominalRate: isNotNil(props.cell) ? Number(props.cell.nominalRate) : undefined,
          apr: isNotNil(props.cell) ? Number(props.cell.apr) : undefined,
          setActive: props.isActive,
        }}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => {
          const nominalRate = formApi.watch('nominalRate');
          // The rates are the only saved values, so an unavailable offer preview (for example a
          // vehicle without a selling price) must not block the save. The preview hides itself.
          const calculation = isNil(nominalRate)
            ? null
            : calculateFinancingOffer({
                pricing: props.pricing,
                term: props.term,
                downPayment: props.downPayment,
                balloonPayment: props.type === 'BALLOON' ? props.balloonPayment : null,
                nominalRate,
                insurance: props.insurance,
              });

          return (
            <VStack spacing={3}>
              <Grid columns={[1, 2]}>
                <FormField
                  control={control}
                  name="nominalRate"
                  type="number"
                  label={i18n.t('page.vehicle.financingAndLeasing.nominalRate')}
                  suffix="%"
                  decimalPlaces={2}
                  minStepperValue={0}
                  isDisabled={props.isReadOnly}
                  isRequired
                />
                <FormField
                  control={control}
                  name="apr"
                  type="number"
                  label={i18n.t('page.vehicle.financingAndLeasing.apr')}
                  suffix="%"
                  decimalPlaces={2}
                  minStepperValue={0}
                  isDisabled={props.isReadOnly}
                  isRequired
                />
              </Grid>

              <Show when={isNotNil(calculation) && props.showCalculation !== false}>
                <Box
                  backgroundColor="palettes.green.10.100"
                  border="1px solid"
                  borderColor="palettes.green.60.100"
                  borderRadius="medium"
                  padding={3}
                  data-testid="financing-cell-result"
                >
                  <VStack spacing={1}>
                    <Text size="xSmall" color="secondary">
                      {i18n.t('page.vehicle.financingAndLeasing.resultingMonthlyRate')}
                    </Text>
                    <HStack spacing={2} align="baseline" wrap>
                      <Heading size={2} color="success">
                        {formatCurrency(calculation?.monthlyRate ?? 0, props.currency)}
                      </Heading>
                    </HStack>
                  </VStack>
                </Box>
              </Show>

              <Show when={props.showCalculation !== false}>
                <Box
                  backgroundColor="palettes.neutral.10.100"
                  border="1px solid"
                  borderColor="general.separator"
                  borderRadius="medium"
                  padding={3}
                  data-testid="financing-cell-calculation"
                >
                  <VStack spacing={1}>
                    <Heading size={4}>
                      {i18n.t('page.vehicle.financingAndLeasing.keyFigures')}
                    </Heading>
                    <KeyFigureValue
                      label={i18n.t('entity.vehicle.labels.sellingPrice')}
                      value={formatCurrencyOrDash(
                        formatCurrency,
                        props.vehiclePrice,
                        props.currency
                      )}
                    />
                    <KeyFigureValue
                      label={i18n.t('page.vehicle.financingAndLeasing.downPayment')}
                      value={`${formatCurrencyOrDash(
                        formatCurrency,
                        downPaymentAmount,
                        props.currency
                      )} (${props.downPayment} %)`}
                    />
                    <KeyFigureValue
                      label={i18n.t('page.vehicle.financingAndLeasing.term')}
                      value={i18n.t('page.vehicle.financingAndLeasing.monthsValue', {
                        term: props.term,
                      })}
                    />
                    <KeyFigureValue
                      label={i18n.t('page.vehicle.financingAndLeasing.netCredit')}
                      value={formatCurrencyOrDash(formatCurrency, netCredit, props.currency)}
                    />
                    <KeyFigureValue
                      label={i18n.t('page.vehicle.financingAndLeasing.totalInterest')}
                      value={formatRateDependentAmount(
                        formatCurrency,
                        calculation?.totalInterest,
                        props.currency
                      )}
                    />
                    <KeyFigureValue
                      label={i18n.t('page.vehicle.financingAndLeasing.grossCredit')}
                      value={formatRateDependentAmount(
                        formatCurrency,
                        calculation?.grossCredit,
                        props.currency
                      )}
                    />
                    <KeyFigureValue
                      label={i18n.t('page.vehicle.financingAndLeasing.totalAmount')}
                      value={formatRateDependentAmount(
                        formatCurrency,
                        calculation?.totalAmount,
                        props.currency
                      )}
                    />
                  </VStack>
                </Box>
              </Show>

              <Show when={!props.isReadOnly}>
                <Show when={props.allowSetActive !== false}>
                  <FormField
                    control={control}
                    name="setActive"
                    type="checkbox"
                    label={i18n.t('page.vehicle.financingAndLeasing.setAsActiveVariant')}
                    isDisabled={props.isActive}
                  />
                </Show>
              </Show>

              <Show when={!props.isReadOnly}>
                <HStack justify="space-between" spacing={3}>
                  <Box>
                    <Show when={props.cell}>
                      <Button
                        title={i18n.t('general.actions.delete')}
                        variant="dangerOutlined"
                        leftIcon="action/delete"
                        aria-label={i18n.t('general.actions.delete')}
                        onClick={() => {
                          if (isNotNil(props.cell)) {
                            props.onDelete(props.cell);
                          }
                        }}
                      />
                    </Show>
                  </Box>
                  <HStack spacing={2}>
                    <Button
                      title={i18n.t('general.actions.discard')}
                      variant="secondary"
                      onClick={props.onClose}
                    />
                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.save')}
                      isDisabled={!formApi.formState.isValid}
                    />
                  </HStack>
                </HStack>
              </Show>
            </VStack>
          );
        }}
      </Form>
    </Dialog>
  );
}
